import type { OrgServiceUsageBreakdown, OrgUsageReportMember, UsageWindowMonth } from '@/services/org-admin';

/**
 * Field selection for the Usage tab's per-member and per-service figures.
 *
 * There used to be a window/cycle toggle here (`UsageScope`) because `?days=N` was a trailing
 * slice that could reach back past a reset — a member whose cycle just started could show 4.2M
 * tokens for "last 30 days" beside $0.00 of a freshly-reset budget, which read as a contradiction
 * with no way for the reader to resolve it. Days mode was dropped from this panel entirely
 * (`useTokenUsageWindow`), leaving only calendar months: `spend_usd_window` for `current` is
 * already the member's own current-cycle period for every real member today (Stripe recurring
 * billing isn't live, so there is no anniversary-anchored cohort where the two would diverge),
 * so the toggle had nothing left to disambiguate. The `_cycle` fields (`spend_usd_cycle` etc.)
 * are still real and still used — just for the "Cycle / budget" column specifically, which is
 * about live budget standing and stays on that clock regardless of which month is selected.
 */

export function windowPeriodLabel(month: UsageWindowMonth): string {
  if (month === 'current') return 'this month';
  if (month === 'previous') return 'last month';
  return 'this quarter';
}

// ── Per-member figures ──────────────────────────────────────────────────────

export function memberTokens(row: OrgUsageReportMember): number {
  return row.prompt_tokens + row.completion_tokens;
}

export function memberTokenSplit(row: OrgUsageReportMember): { input: number; output: number } {
  return { input: row.prompt_tokens, output: row.completion_tokens };
}

export function memberSpend(row: OrgUsageReportMember): number {
  return row.spend_usd_window;
}

// ── Per-service figures ─────────────────────────────────────────────────────

export type ServiceMetric = 'runs' | 'failed' | 'input' | 'output' | 'cost';

const WINDOW_FIELD: Record<ServiceMetric, keyof OrgServiceUsageBreakdown> = {
  runs: 'run_count',
  failed: 'failed_count',
  input: 'input_tokens',
  output: 'output_tokens',
  cost: 'cost_usd',
};

/** A per-service figure. Window fields are always present on the wire, unlike the `_cycle` ones. */
export function serviceMetric(row: OrgServiceUsageBreakdown, metric: ServiceMetric): number {
  return row[WINDOW_FIELD[metric]] as number;
}
